import React, { useMemo } from 'react';
import { Stack } from '@mui/material';
import { SelectField } from 'components/form';
import { blue, grey } from '@mui/material/colors';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { IndicatorMenu, IndicatorMenuOption } from 'components/shared/IndicatorMenu';

import { CustomerVisibility } from '../../../enums';
import { DealSheetFormValues, LineItemSection } from '../types';

interface Props {
  rowIndex: number;
  section: LineItemSection;
}

const VISIBILITY_OPTIONS: IndicatorMenuOption<CustomerVisibility>[] = [
  {
    value: CustomerVisibility.Visible,
    label: 'Visible to customer',
    color: grey[500],
    renderIcon: (color: string) => <Visibility sx={{ color, fontSize: '1.5rem' }} />,
  },
  {
    value: CustomerVisibility.InternalOnly,
    label: 'Internal only',
    color: blue[500],
    renderIcon: (color: string) => <VisibilityOff sx={{ color, fontSize: '1.5rem' }} />,
  },
];

export const LineItemVisibilityCell = ({ rowIndex, section }: Props) => {
  const { control, setValue } = useFormContext<DealSheetFormValues>();
  const { move } = useFieldArray({ control, name: section });

  const items = useWatch({ control, name: section });
  const customerVisibility = useWatch({ control, name: `${section}.${rowIndex}.customerVisibility` });

  const currentId = items?.[rowIndex]?.id;
  const isInternalOnly = customerVisibility === CustomerVisibility.InternalOnly;

  const parentOptions = useMemo(() => {
    if (!items) return [];

    return items
      .filter(
        item => item.id !== currentId && !!item.name && item.customerVisibility !== CustomerVisibility.InternalOnly
      )
      .map(item => ({ label: item.name, value: item.id }));
  }, [items, currentId]);

  const handleVisibilityChange = (next: CustomerVisibility) => {
    setValue(`${section}.${rowIndex}.customerVisibility`, next, { shouldValidate: true });

    if (next !== CustomerVisibility.InternalOnly) {
      setValue(`${section}.${rowIndex}.parentId`, null, { shouldValidate: true });
      const lastIndex = (items?.length ?? 1) - 1;
      if (rowIndex !== lastIndex) move(rowIndex, lastIndex);
    }
  };

  const handleParentChange = (nextParentId: string | number | boolean) => {
    if (!items || typeof nextParentId !== 'string' || !nextParentId) return;

    const targetIndex = getEndOfParentGroupIndex(items, nextParentId, rowIndex);
    if (targetIndex !== rowIndex) move(rowIndex, targetIndex);
  };

  return (
    <Stack gap="0.5rem">
      <IndicatorMenu value={customerVisibility} options={VISIBILITY_OPTIONS} onChange={handleVisibilityChange} />

      {isInternalOnly && (
        <SelectField
          control={control}
          variant="outlined"
          label="Roll up into"
          options={parentOptions}
          extraAction={handleParentChange}
          name={`${section}.${rowIndex}.parentId`}
        />
      )}
    </Stack>
  );
};

type LineItemRow = { id: string; parentId: string | null };

function getEndOfParentGroupIndex(items: LineItemRow[], newParentId: string, currentRowIndex: number): number {
  const parentIndex = items.findIndex(item => item.id === newParentId);
  if (parentIndex === -1) return currentRowIndex;

  let lastOfGroupIndex = parentIndex;

  items.forEach((item, index) => {
    if (index === currentRowIndex) return;
    if (item.parentId === newParentId) lastOfGroupIndex = index;
  });

  return currentRowIndex > lastOfGroupIndex ? lastOfGroupIndex + 1 : lastOfGroupIndex;
}
